import { Timer, logAxiosError } from 'libs/logging-ts';
import { AxiosSignedConnectionSource } from 'libs/service-access-lib';
import { Service, SignedConnectionSource, WaymarkServiceAccessKey } from 'libs/shared-types';
import { SimpleExpiringCache } from 'libs/ts-simple-cache';

const CACHE_TTL = 60 * 60 * 1000; // one hour
const serviceCache = new SimpleExpiringCache(CACHE_TTL);

export class ServiceDiscoverySDK {
  private serviceAccessKey: WaymarkServiceAccessKey;
  private connectionSource: SignedConnectionSource;

  constructor(serviceAccessKey: WaymarkServiceAccessKey) {
    this.serviceAccessKey = serviceAccessKey;
    this.connectionSource = new AxiosSignedConnectionSource(serviceAccessKey.identity);
  }

  async discoverService(serviceName: string): Promise<Service> {
    const timer = new Timer({
      metadata: { service: 'service-discovery-sdk.discoverService' },
    });

    const cachedService = serviceCache.get(serviceName);
    if (cachedService) {
      timer.debug('Service found in cache', { serviceName });
      return cachedService as Service;
    }

    const connection = await this.connectionSource.getSignedConnection();
    const request = { serviceName };

    try {
      const response = await connection.get(
        `${this.serviceAccessKey.serviceDiscoveryEndpoint}/discover`,
        { params: request },
      );

      serviceCache.set(serviceName, response.data);

      return response.data;
    } catch (error) {
      logAxiosError({
        logger: timer,
        error,
        logResponseData: true,
        message: 'Service discovery failed',
        metadata: { serviceName },
      });

      throw error;
    } finally {
      timer.debug('discoverService finished', { serviceName });
    }
  }
}
